import { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase';
import { collection, getDocs, doc, updateDoc, getDoc, addDoc, deleteDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { FiSmartphone, FiUsers } from 'react-icons/fi';

// Componentes
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminNavbar from '../components/admin/AdminNavbar';
import StatsGrid from '../components/admin/StatsGrid';
import AppsTable from '../components/admin/AppsTable';
import UsersTable from '../components/admin/UsersTable';
import AdminAnalyticsPanel from '../components/admin/AdminAnalyticsPanel';
import AppDetailsModal from '../components/admin/AppDetailsModal';
import AlertModal from '../components/admin/AlertModal';
import VerificationModal from '../components/admin/VerificationModal';
import PromotionsManager from '../components/admin/PromotionsManager';
import LoadingScreen from '../components/admin/LoadingScreen';
import AccessDenied from '../components/admin/AccessDenied';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [allApps, setAllApps] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalApps: 0,
    pendingApps: 0,
    publishedApps: 0,
    totalUsers: 0,
    adminUsers: 0
  });

  // Estados para filtros e busca
  // eslint-disable-next-line no-unused-vars
  const [searchTerm, setSearchTerm] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [statusFilter, setStatusFilter] = useState('all');
  // eslint-disable-next-line no-unused-vars
  const [roleFilter, setRoleFilter] = useState('all');

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Estados para modais
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  // Sidebar responsivity states
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const calculateStats = (apps, users) => {
    const pendingApps = apps.filter(app => app.status === 'pending').length;
    const publishedApps = apps.filter(app => app.status === 'published' || app.status === 'active').length;
    const adminUsers = users.filter(user => user.isAdmin).length;

    setStats({
      totalApps: apps.length,
      pendingApps,
      publishedApps,
      totalUsers: users.length,
      adminUsers
    });
  };

  const fetchData = useCallback(async () => {
    console.log('🔄 Iniciando fetchData...');
    
    if (!isAdmin) {
      console.log('⏸️ FetchData pausado - usuário não é admin');
      return;
    }

    setDataLoading(true);
    try {
      const appsQuery = query(collection(db, 'apps'), orderBy('createdAt', 'desc'));
      const appsSnapshot = await getDocs(appsQuery);
      const appsList = appsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt?.seconds * 1000),
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : new Date(doc.data().updatedAt?.seconds * 1000)
      }));
      setAllApps(appsList);

      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt?.seconds * 1000)
      }));
      setAllUsers(usersList);

      calculateStats(appsList, usersList);
      
    } catch (error) {
      console.error('❌ Erro ao buscar dados:', error);
    } finally {
      setDataLoading(false);
      console.log('✅ FetchData finalizado');
    }
  }, [isAdmin]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists() && userDoc.data().isAdmin === true) {
            setIsAdmin(true);
          } else {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('❌ Erro ao verificar status de admin:', error);
          navigate('/dashboard');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    };

    checkAdminStatus();
  }, [navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, fetchData]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Desktop: sidebar aberto por padrão, Mobile: fechado
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (allApps.length === 0 && allUsers.length === 0) return;

    let filtered = activeTab === 'apps' ? [...allApps] : [...allUsers];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        activeTab === 'apps'
          ? item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())
          : item.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (activeTab === 'apps' && statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    if (activeTab === 'users' && roleFilter !== 'all') {
      filtered = filtered.filter(user =>
        roleFilter === 'admin' ? user.isAdmin : !user.isAdmin
      );
    }

    if (activeTab === 'apps') {
      setFilteredApps(filtered);
    } else {
      setFilteredUsers(filtered);
    }
    setCurrentPage(1);
  }, [allApps, allUsers, searchTerm, statusFilter, roleFilter, activeTab]);

  // eslint-disable-next-line no-unused-vars
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  const updateAppStatus = async (appId, newStatus) => {
    try {
      await updateDoc(doc(db, 'apps', appId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      const updatedApps = allApps.map(app =>
        app.id === appId ? { ...app, status: newStatus, updatedAt: new Date() } : app
      );
      setAllApps(updatedApps);
      calculateStats(updatedApps, allUsers);
    } catch (error) {
      console.error('Erro ao atualizar status do app:', error);
    }
  };

  const toggleUserAdminStatus = async (userId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        isAdmin: !currentStatus,
        updatedAt: serverTimestamp()
      });
      const updatedUsers = allUsers.map(user =>
        user.id === userId ? { ...user, isAdmin: !currentStatus } : user
      );
      setAllUsers(updatedUsers);
      calculateStats(allApps, updatedUsers);
    } catch (error) {
      console.error('Erro ao atualizar status de admin:', error);
    }
  };

  const updateOneSignalUrl = async (userId, newUrl) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        onesignalUrl: newUrl,
        updatedAt: serverTimestamp()
      });
      setAllUsers(allUsers.map(user =>
        user.id === userId ? { ...user, onesignalUrl: newUrl } : user
      ));
    } catch (error) {
      console.error('Erro ao atualizar URL do OneSignal:', error);
    }
  };

  const sendAlert = async (userId, appId = null) => {
    if (!alertMessage.trim()) {
      alert('Por favor, digite uma mensagem para o alerta.');
      return;
    }
    try {
      await addDoc(collection(db, 'adminAlerts'), {
        userId, appId, title: 'Alerta Administrativo', message: alertMessage,
        priority: 'medium', read: false, notified: false,
        createdAt: serverTimestamp(), expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      });
      alert('Alerta enviado com sucesso!');
      setAlertModalOpen(false);
      setAlertMessage('');
    } catch (error) {
      console.error('Erro ao enviar alerta:', error);
      alert('Erro ao enviar alerta. Tente novamente.');
    }
  };

  const deleteApp = async (appId) => {
    try {
      await deleteDoc(doc(db, 'apps', appId));
      try { await deleteDoc(doc(db, 'appMetrics', appId)); } catch (e) {}
      const updatedApps = allApps.filter(app => app.id !== appId);
      setAllApps(updatedApps);
      calculateStats(updatedApps, allUsers);
      alert('Aplicativo deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar aplicativo:', error);
      alert('Erro ao deletar aplicativo. Tente novamente.');
    }
  };

  const deleteUser = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (!userDoc.exists()) return;
      const userEmail = userDoc.data().email;
      const userApps = allApps.filter(app => app.userEmail === userEmail);
      if (userApps.length > 0) {
        alert(`❌ Não é possível deletar o usuário! Ele possui ${userApps.length} aplicativo(s).`);
        return;
      }
      await deleteDoc(doc(db, 'users', userId));
      const updatedUsers = allUsers.filter(user => user.id !== userId);
      setAllUsers(updatedUsers);
      calculateStats(allApps, updatedUsers);
      alert('✅ Usuário deletado com sucesso do Firestore!');
    } catch (error) {
      console.error('Erro ao deletar usuário:', error);
      alert('❌ Erro ao deletar usuário. Tente novamente.');
    }
  };

  const handleNotificationClick = (item) => {
    if (item.type === 'user') {
      setSelectedUser(item);
      setActiveTab('users'); 
      setVerificationModalOpen(true);
    } else if (item.type === 'app') {
      setSelectedApp(item);
      setActiveTab('apps');
    }
  };

  const handleVerifyUser = async () => {
    if (!selectedUser) return;
    try {
      await updateDoc(doc(db, 'users', selectedUser.id), {
        isNewUser: false,
        userStatus: 'verified',
        updatedAt: serverTimestamp()
      });
      const updatedUsers = allUsers.map(user => 
        user.id === selectedUser.id 
          ? { ...user, isNewUser: false, userStatus: 'verified' }
          : user
      );
      setAllUsers(updatedUsers);
      setVerificationModalOpen(false);
    } catch (error) {
      console.error('Erro ao verificar usuário:', error);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = activeTab === 'apps' ? filteredApps.slice(indexOfFirstItem, indexOfLastItem) : filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil((activeTab === 'apps' ? filteredApps.length : filteredUsers.length) / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getTitle = (tab) => {
    switch(tab) {
      case 'dashboard': return '';
      case 'apps': return 'Gerenciar Aplicativos';
      case 'users': return 'Gerenciar Usuários';
      case 'promotions': return 'Gerenciar Promoções';
      case 'analytics': return 'Analytics Global';
      default: return 'Admin Panel';
    }
  };

  if (loading) return <LoadingScreen />;
  if (!isAdmin) return <AccessDenied />;

  return (
    <div className={`flex h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-[#fafafa] text-gray-900'} overflow-hidden transition-colors duration-200 w-full`}>
      
      {/* Sidebar Integrada Responsiva */}
      <AdminSidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isMobile={isMobile}
      />

      {/* Main Content */}
      <div className="
          flex-1 
          w-full
          p-4 sm:p-6 md:p-8 lg:p-10
          overflow-y-auto overflow-x-hidden custom-scrollbar
        ">
        <div className="max-w-7xl mx-auto w-full space-y-6">
          <AdminNavbar 
            title={getTitle(activeTab)} 
            toggleSidebar={() => setSidebarOpen(!sidebarOpen)} 
            onNotificationClick={handleNotificationClick}
            isMobile={isMobile}
          />
          
          {/* Dashboard View - Global Analytics */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              <StatsGrid stats={stats} setActiveTab={setActiveTab} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                 {/* Quick Actions */}
                 <div className={`p-5 sm:p-6 md:p-8 border flex flex-col justify-between ${isDarkMode ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                    <h3 className={`text-xs font-bold uppercase tracking-widest mb-4 sm:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Ações Estratégicas</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                       <button onClick={() => setActiveTab('apps')} className={`p-4 sm:p-6 border transition-all duration-300 group flex sm:flex-col items-center sm:items-start gap-3 sm:gap-4 ${isDarkMode ? 'border-gray-800 hover:border-indigo-500 bg-gray-900' : 'border-gray-200 hover:border-indigo-500 hover:shadow-[4px_4px_0px_0px_rgba(99,102,241,0.2)] bg-gray-50 hover:bg-white'}`}>
                          <div className={`p-2.5 sm:p-3 border rounded-lg ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-white shadow-sm'}`}>
                            <FiSmartphone size={20} className={`sm:w-6 sm:h-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                          </div>
                          <span className={`text-left text-sm font-semibold sm:mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Gerenciar<br className="hidden sm:block"/> Aplicativos</span>
                       </button>
                       <button onClick={() => setActiveTab('users')} className={`p-4 sm:p-6 border transition-all duration-300 group flex sm:flex-col items-center sm:items-start gap-3 sm:gap-4 ${isDarkMode ? 'border-gray-800 hover:border-emerald-500 bg-gray-900' : 'border-gray-200 hover:border-emerald-500 hover:shadow-[4px_4px_0px_0px_rgba(16,185,129,0.2)] bg-gray-50 hover:bg-white'}`}>
                          <div className={`p-2.5 sm:p-3 border rounded-lg ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-white shadow-sm'}`}>
                            <FiUsers size={20} className={`sm:w-6 sm:h-6 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                          </div>
                          <span className={`text-left text-sm font-semibold sm:mt-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Gerenciar<br className="hidden sm:block"/> Usuários</span>
                       </button>
                    </div>
                 </div>
                 {/* Analytics Preview */}
                 <div className={`p-5 sm:p-6 md:p-8 border flex flex-col justify-between group ${isDarkMode ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                    <div>
                       <h3 className={`text-xs font-bold uppercase tracking-widest mb-3 sm:mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Deep Analytics</h3>
                       <p className={`text-sm sm:text-base lg:text-lg font-light leading-relaxed mb-6 sm:mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                         Acesse relatórios granulares sobre o comportamento da plataforma, downloads por região e retenção de usuários a longo prazo.
                       </p>
                    </div>
                    <button onClick={() => setActiveTab('analytics')} className={`w-full py-4 sm:py-5 font-bold tracking-widest uppercase text-[10px] sm:text-xs border transition-all duration-300 ${isDarkMode ? 'border-white text-black bg-white hover:bg-gray-200' : 'border-gray-900 text-white bg-gray-900 hover:bg-black hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)]'}`}>
                       Abrir Console Analytics
                    </button>
                 </div>
              </div>
            </div>
          )}

          {/* List Views (Apps & Users) - v1 */}
          {(activeTab === 'apps' || activeTab === 'users') && (
            <div className="space-y-6">
              
              {/* Toolbar / Filters - Comentado a pedido do usuário
              <div className={`p-4 sm:p-5 border flex flex-col md:flex-row gap-4 justify-between items-center ${isDarkMode ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200'}`}>
                 <div className="relative w-full md:w-96">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input 
                       type="text" 
                       placeholder={activeTab === 'apps' ? "Buscar app..." : "Buscar usuário..."}
                       value={searchTerm}
                       onChange={(e) => setSearchTerm(e.target.value)}
                       className={`w-full pl-10 pr-4 py-2.5 text-sm border focus:outline-none focus:ring-0 focus:border-indigo-500 transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                    />
                 </div>
                 
                 <div className="flex items-center gap-3 w-full md:w-auto">
                    <div className="relative">
                       <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                       <select 
                          value={activeTab === 'apps' ? statusFilter : roleFilter}
                          onChange={(e) => activeTab === 'apps' ? setStatusFilter(e.target.value) : setRoleFilter(e.target.value)}
                          className={`pl-10 pr-8 py-2.5 text-sm border appearance-none focus:outline-none focus:ring-0 focus:border-indigo-500 transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                       >
                          <option value="all">Todos</option>
                          {activeTab === 'apps' ? (
                             <>
                                <option value="pending">Pendente</option>
                                <option value="published">Publicado</option>
                                <option value="development">Em Desenv.</option>
                                <option value="active">Ativo</option>
                             </>
                          ) : (
                             <>
                                <option value="admin">Admin</option>
                                <option value="user">Usuário</option>
                             </>
                          )}
                       </select>
                    </div>

                    <button 
                       onClick={handleRefresh} 
                       disabled={isRefreshing}
                       className={`p-2 rounded-lg border transition-all ${isDarkMode ? 'border-gray-700 hover:bg-gray-700 text-gray-300' : 'border-gray-200 hover:bg-gray-50 text-gray-600'}`}
                       title="Atualizar lista"
                    >
                       <FiRefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                 </div>
              </div>
              */}

              {/* Table Content */}
              <div>
                 {dataLoading ? (
                    <div className="p-12 flex justify-center">
                       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                 ) : (
                    <>
                       {activeTab === 'apps' && (
                          <AppsTable
                             currentItems={currentItems}
                             allUsers={allUsers}
                             updateAppStatus={updateAppStatus}
                             setSelectedApp={setSelectedApp}
                             setSelectedUser={setSelectedUser}
                             setAlertModalOpen={setAlertModalOpen}
                             selectedApp={selectedApp}
                             filteredApps={filteredApps}
                             currentPage={currentPage}
                             itemsPerPage={itemsPerPage}
                             indexOfFirstItem={indexOfFirstItem}
                             indexOfLastItem={indexOfLastItem}
                             totalPages={totalPages}
                             paginate={paginate}
                             deleteApp={deleteApp}
                          />
                       )}
                       {activeTab === 'users' && (
                          <UsersTable
                             currentItems={currentItems}
                             updateOneSignalUrl={updateOneSignalUrl}
                             toggleUserAdminStatus={toggleUserAdminStatus}
                             setSelectedUser={setSelectedUser}
                             setAlertModalOpen={setAlertModalOpen}
                             filteredUsers={filteredUsers}
                             currentPage={currentPage}
                             itemsPerPage={itemsPerPage}
                             indexOfFirstItem={indexOfFirstItem}
                             indexOfLastItem={indexOfLastItem}
                             totalPages={totalPages}
                             paginate={paginate}
                             deleteUser={deleteUser}
                          />
                       )}
                    </>
                 )}
              </div>
            </div>
          )}

          {/* Promotions View */}
          {activeTab === 'promotions' && (
             <div className="space-y-6">
                <PromotionsManager />
             </div>
          )}

          {/* Analytics View */}
          {activeTab === 'analytics' && (
             <div className="space-y-6">
                <AdminAnalyticsPanel />
             </div>
          )}

        </div>
      </div>

      {/* Modals */}
      <AppDetailsModal
        selectedApp={selectedApp}
        allUsers={allUsers}
        setSelectedApp={setSelectedApp}
        setSelectedUser={setSelectedUser}
        setAlertModalOpen={setAlertModalOpen}
      />

      <VerificationModal
        isOpen={verificationModalOpen}
        onClose={() => setVerificationModalOpen(false)}
        onConfirm={handleVerifyUser}
        userName={selectedUser?.fullName || 'Usuário'}
        userEmail={selectedUser?.email || ''}
      />

      <AlertModal
        alertModalOpen={alertModalOpen}
        setAlertModalOpen={setAlertModalOpen}
        alertMessage={alertMessage}
        setAlertMessage={setAlertMessage}
        selectedUser={selectedUser}
        selectedApp={selectedApp}
        sendAlert={sendAlert}
      />
    </div>
  );
}

export default AdminPanel;
